import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddCaseIcon,
  AddDomainIcon,
  Button,
  Flags,
  GroupAddIcon,
  PlusIcon,
  PopoverMenu,
  UploadFileIcon,
  useFlag,
} from 'ui';
import { InvitationModalsOrchestrator } from '@components/InvitationModalsOrchestrator';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { PATHS } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';

const AdminRoles = [UserRole.Admin, UserRole.Owner];

export function AddOptionsButton() {
  const isUploadClaimsFileEnabled = useFlag(Flags.uploadClaimsFile);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const navigate = useNavigate();
  const anchorEl = useRef<HTMLButtonElement>(null);

  const { getUserRole } = useUserOrgCase();

  const organizationRole = getUserRole(RoleLevel.organization);
  const caseRole = getUserRole(RoleLevel.case);

  const isCurrentOrgAdmin =
    organizationRole !== undefined && AdminRoles.includes(organizationRole);

  const isCurrentCaseAdmin =
    caseRole !== undefined && AdminRoles.includes(caseRole);

  const { selectedOrganization } = useUserOrgCase();

  const [inviteOrgModalOpen, setInviteOrgModalOpen] = useState(false);
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);

  const menuOptions = useMemo(
    () => [
      {
        onClick: () => {
          navigate(
            `${PATHS.PRIVATE_BASE.newCaseCreation}?orgId=${selectedOrganization.organizationID}`,
            { state: { previousUrl: location.pathname } },
          );
        },
        icon: <AddCaseIcon />,
        iconPosition: 'start' as const,
        title: 'Create New Case',
        visible: isCurrentOrgAdmin,
      },
      {
        divider: true,
        visible: isCurrentOrgAdmin,
      },
      {
        onClick: () => setInviteOrgModalOpen(true),
        iconPosition: 'start' as const,
        icon: <AddDomainIcon />,
        title: 'Add Organization Member',
        visible: isCurrentOrgAdmin,
      },
      {
        onClick: () => setInviteUserModalOpen(true),
        iconPosition: 'start' as const,
        icon: <GroupAddIcon />,
        title: 'Add Case Member',
        visible: isCurrentCaseAdmin,
      },
      {
        divider: true,
        visible: isCurrentOrgAdmin,
      },
      {
        onClick: () => {
          navigate(
            `${PATHS.PRIVATE_BASE.uploadClaimsFile}?orgId=${selectedOrganization.organizationID}`,
            { state: { previousUrl: location.pathname } },
          );
        },
        icon: <UploadFileIcon />,
        iconPosition: 'start' as const,
        visible: isCurrentCaseAdmin,
        title: 'Upload Claims File',
        disabled: !isUploadClaimsFileEnabled,
      },
    ],
    [
      isCurrentCaseAdmin,
      isCurrentOrgAdmin,
      isUploadClaimsFileEnabled,
      navigate,
      selectedOrganization.organizationID,
    ],
  );

  return (
    <>
      <Button
        aria-label="Add Options Dropdown"
        color="neutral"
        onClick={() => {
          setIsPopoverOpen((prev) => !prev);
        }}
        ref={anchorEl}
        startIcon={<PlusIcon />}
        variant="text"
      />
      <PopoverMenu
        anchorEl={anchorEl.current}
        isOpen={isPopoverOpen}
        menuItems={menuOptions}
        onClose={() => {
          setIsPopoverOpen(false);
        }}
      />
      <InvitationModalsOrchestrator
        inviteOrgModalOpen={inviteOrgModalOpen}
        inviteUserModalOpen={inviteUserModalOpen}
        setInviteOrgModalOpen={setInviteOrgModalOpen}
        setInviteUserModalOpen={setInviteUserModalOpen}
      />
    </>
  );
}
